import React, { FC, Fragment, useState } from 'react';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { Box } from '@adsk/alloy-react-box';
import { callOrReturn } from '@adsk/alloy-react-helpers';

import MissingEntities from './MissingEntities';
import ReferencePickerBlockTitle from './ReferencePickerBlockTitle';
import callUnique from '../helpers/callUnique';
import { DataToConfirmRemoving } from '../hooks/useRemoveEntity';
import { useTranslation } from '../i18n';
import ReferencePickerError from '../ReferencePickerError';
import ReferencePickerLoader from '../ReferencePickerLoader';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';
import type {
  EntityWithId,
  EntityRelationship,
  RelationshipItemWithMetaData,
  EntityMappingById,
  UpdateEntitiesInCache,
} from '../types/types';

interface ReferencePickerBlockGroupProps {
  plugin: CommonReferencePickerPlugin;
  relationships: RelationshipItemWithMetaData[];
  style?: React.CSSProperties;
  className?: string;
  entitiesCache?: EntityMappingById;
  updateEntitiesInCache?: UpdateEntitiesInCache;
  allowRemoveLinkedEntity?: boolean | ((args: EntityRelationship) => boolean);
  setDataToConfirmRemoving: (args: DataToConfirmRemoving) => void;
  onReferenceClickAnalytics?: (args: {
    id: string;
    domain: string;
    type: string;
  }) => void;
  sourceEntityId: string;
  sourceEntityDomain: string;
  sourceEntityType: string;
  isAccordionVariant?: boolean;
}

const ReferencePickerBlockGroup: FC<ReferencePickerBlockGroupProps> = ({
  plugin,
  relationships,
  style,
  className,
  entitiesCache,
  updateEntitiesInCache,
  allowRemoveLinkedEntity,
  setDataToConfirmRemoving,
  onReferenceClickAnalytics,
  sourceEntityId,
  sourceEntityDomain,
  sourceEntityType,
  isAccordionVariant,
}) => {
  const { t } = useTranslation();
  const projectId = useCurrentProjectId() ?? '';
  const [previewEntity, setPreviewEntity] = useState<EntityWithId>();

  const entityIds = relationships.map(({ entityId }) => entityId);
  const loading = entityIds.some((id) => entitiesCache?.[id]?.loading);
  const error = entityIds.some((id) => entitiesCache?.[id]?.error);

  const retry = () =>
    callUnique(
      entityIds.map((id) => entitiesCache?.[id]?.retry).filter((r) => !!r),
    );

  const data = relationships
    .map(({ relationship, entityId }) => ({
      relationship,
      entity: entitiesCache?.[entityId]?.entity,
    }))
    .filter((e): e is EntityRelationship => !!e.entity);
  const hasData = !!data.length;
  const missingEntitiesCount = relationships.length - data.length;
  const showMissingEntities =
    plugin.alwaysShowCustomMissingEntities ||
    (!!missingEntitiesCount && !loading && !error);

  const { EntityBlock, EntityPreviewModal, PluralText, metadata } = plugin;
  const EmptyLoader = plugin.CustomBlockEmptyLoader || ReferencePickerLoader;
  const Error = plugin.CustomBlockError || ReferencePickerError;
  const MissingEntitiesComponent =
    plugin.CustomMissingEntities || MissingEntities;
  const showPreviewModal = !!EntityPreviewModal;

  const onUpdate = (entity: EntityWithId) => {
    updateEntitiesInCache?.({
      entities: [entity],
      type: metadata.type,
      domain: metadata.domain,
    });
  };

  return (
    <Box className={className} style={style}>
      {!isAccordionVariant && (
        <ReferencePickerBlockTitle>
          <PluralText />
        </ReferencePickerBlockTitle>
      )}
      {hasData && (
        <Fragment>
          <div data-testid="reference-picker-block-group">
            {data.map(({ relationship, entity }) => (
              <EntityBlock
                key={entity.id}
                entity={entity}
                projectId={projectId}
                onClick={
                  showPreviewModal ? () => setPreviewEntity(entity) : undefined
                }
                onReferenceClickAnalytics={
                  onReferenceClickAnalytics
                    ? () =>
                        onReferenceClickAnalytics({
                          id: entity.id,
                          domain: metadata.domain,
                          type: metadata.type,
                        })
                    : undefined
                }
                onRemoveClick={
                  callOrReturn(allowRemoveLinkedEntity, {
                    entity,
                    relationship,
                  })
                    ? () =>
                        setDataToConfirmRemoving({
                          entity,
                          plugin,
                          relationship,
                        })
                    : undefined
                }
                sourceEntityId={sourceEntityId}
                sourceEntityDomain={sourceEntityDomain}
                sourceEntityType={sourceEntityType}
              />
            ))}
          </div>
          {loading && <ReferencePickerLoader />}
          {error && (
            <ReferencePickerError onTryAgain={retry}>
              {t('ERROR_LOADING_MORE')}
            </ReferencePickerError>
          )}
        </Fragment>
      )}
      {!hasData && loading && <EmptyLoader />}
      {!hasData && error && <Error onTryAgain={retry} />}
      {showMissingEntities && (
        <MissingEntitiesComponent
          loading={loading}
          error={error}
          data={data}
          count={missingEntitiesCount}
          relationshipCount={relationships.length}
        />
      )}
      {showPreviewModal && (
        <EntityPreviewModal
          entity={previewEntity}
          open={!!previewEntity}
          onUpdate={onUpdate}
          onClose={() => setPreviewEntity(undefined)}
        />
      )}
    </Box>
  );
};

export default ReferencePickerBlockGroup;
